import { useState } from "react";
import { MuiTable, TableCell } from "src/components/MuiTable/MuiTable.tsx";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type { SupplierOrderPositionResource } from "@exonn/js-sdk";
import { Panel } from "src/components/Panel/Panel.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { Input } from "src/components/Input/Input.tsx";
import { Flex } from "src/components/Flex.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { useResourceFormData } from "src/components/Form/hooks/useResourceFormData.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import type { SupplierOrderFormData } from "src/admin/modules/supplier-order/forms/supplier-order/config.tsx";
import { useSupplierOrderAction } from "src/admin/modules/supplier-order/forms/supplier-order/hooks/useSupplierOrderAction.ts";
import { ReasonDialog } from "src/admin/modules/supplier-order/forms/supplier-order/components/ReasonDialog.tsx";
import { AgreementSelect } from "src/admin/modules/supplier-order/forms/supplier-order/tabs/positions/AgreementSelect.tsx";

const quantity = (value: string | null) =>
  value === null ? "—" : Number(value).toLocaleString("de-DE");
const price = (value: string) =>
  Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });

const awaitingReceipt = ["sent", "confirmed", "partially_received"];

/**
 * Позиции заказа. В черновике — правка количества, цены и даты прямо в таблице (сохраняется
 * при выходе из поля) и добавление артикула из соглашений поставщика. Упаковку, цену по ступеням
 * и снимок данных артикула проверяет и заполняет бэкенд. После отправки — short close остатка.
 */
export const Positions = () => {
  const { t } = useResourceFormTranslation();
  const data = useResourceFormData<SupplierOrderFormData>();
  const order = data?.data;
  const { mutate, isPending } = useSupplierOrderAction(order?.id);
  const [agreementId, setAgreementId] = useState("");
  const [newQuantity, setNewQuantity] = useState("");
  const [closing, setClosing] = useState<SupplierOrderPositionResource | null>(
    null,
  );

  if (!order?.id) {
    return null;
  }

  const editable = order.is_editable;
  const positions = order.positions ?? [];
  const hasLanded = positions.some(
    (position) => position.landed_unit_price !== null,
  );
  const currency = order.currency?.iso ?? "";

  const update = (
    position: SupplierOrderPositionResource,
    field: "quantity_ordered" | "unit_price" | "requested_date",
    value: string,
  ) => {
    const current = position[field] ?? "";
    const unchanged =
      field === "requested_date"
        ? value === current
        : value !== "" && Number(value) === Number(current);
    if (unchanged || (field !== "requested_date" && value === "")) {
      return;
    }
    mutate({
      method: "put",
      url: `/supplier-order-position/${position.id}`,
      body: {
        [field]: field === "requested_date" && value === "" ? null : value,
      },
    });
  };

  const add = () =>
    mutate(
      {
        url: "/supplier-order-position",
        body: {
          supplier_order_id: order.id,
          supplier_agreement_id: agreementId,
          quantity_ordered: newQuantity,
        },
      },
      {
        onSuccess: () => {
          setAgreementId("");
          setNewQuantity("");
        },
      },
    );

  return (
    <div style={{ padding: 15 }}>
      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Panel.Icon>
              <span className={"icon-15-basic_info"} />
            </Panel.Icon>
            {t("positions.title")}
          </Panel.Title>
        </Panel.Header>
        <Panel.Content>
          {positions.length === 0 ? (
            <TextV2 variant={"body1"}>{t("positions.empty")}</TextV2>
          ) : (
            <MuiTable size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("positions.position")}</TableCell>
                  <TableCell>{t("positions.supplier-sku")}</TableCell>
                  <TableCell>{t("positions.title-column")}</TableCell>
                  <TableCell>{t("positions.package")}</TableCell>
                  <TableCell>{t("positions.quantity")}</TableCell>
                  <TableCell>
                    {t("positions.unit-price", { currency })}
                  </TableCell>
                  <TableCell>{t("positions.vat")}</TableCell>
                  <TableCell>
                    {t("positions.net-total", { currency })}
                  </TableCell>
                  <TableCell>{t("positions.requested-date")}</TableCell>
                  {!editable && (
                    <TableCell>{t("positions.received")}</TableCell>
                  )}
                  {!editable && <TableCell>{t("positions.open")}</TableCell>}
                  {/* Засчитано в Gutschrift / Aufstellung поставщику (5.2). */}
                  {!editable && (
                    <TableCell>{t("positions.credited")}</TableCell>
                  )}
                  <TableCell>{t("positions.status")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((position) => (
                  <TableRow
                    key={`${position.id}-${position.quantity_ordered}-${position.unit_price}`}
                  >
                    <TableCell>{position.position}</TableCell>
                    <TableCell>{position.supplier_sku}</TableCell>
                    <TableCell>
                      {position.title}
                      {position.option_value
                        ? ` (${position.option_value})`
                        : ""}
                      {position.ean ? (
                        <div style={{ opacity: 0.6 }}>{position.ean}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {position.amount_of_unit_per_package
                        ? Number(position.amount_of_unit_per_package)
                        : "—"}
                    </TableCell>
                    <TableCell style={{ minWidth: 90 }}>
                      {editable ? (
                        <Input
                          type={"number"}
                          min={0}
                          defaultValue={String(
                            Number(position.quantity_ordered),
                          )}
                          onBlur={(event) =>
                            update(
                              position,
                              "quantity_ordered",
                              event.target.value,
                            )
                          }
                        />
                      ) : (
                        quantity(position.quantity_ordered)
                      )}
                    </TableCell>
                    <TableCell style={{ minWidth: 100 }}>
                      {editable ? (
                        <Input
                          type={"number"}
                          min={0}
                          defaultValue={String(Number(position.unit_price))}
                          onBlur={(event) =>
                            update(position, "unit_price", event.target.value)
                          }
                        />
                      ) : (
                        price(position.unit_price)
                      )}
                    </TableCell>
                    <TableCell>{Number(position.vat_percent)} %</TableCell>
                    <TableCell>{price(position.net_total)}</TableCell>
                    {hasLanded && (
                      <TableCell>
                        {position.landed_unit_price === null
                          ? "—"
                          : price(position.landed_unit_price)}
                      </TableCell>
                    )}
                    <TableCell style={{ minWidth: 130 }}>
                      {editable ? (
                        <Input
                          type={"date"}
                          defaultValue={position.requested_date ?? ""}
                          onBlur={(event) =>
                            update(
                              position,
                              "requested_date",
                              event.target.value,
                            )
                          }
                        />
                      ) : (
                        position.requested_date ?? "—"
                      )}
                    </TableCell>
                    {!editable && (
                      <TableCell>
                        {quantity(position.quantity_received)}
                      </TableCell>
                    )}
                    {!editable && (
                      <TableCell>{quantity(position.quantity_open)}</TableCell>
                    )}
                    {!editable && (
                      <TableCell>
                        {quantity(position.quantity_credited)}
                      </TableCell>
                    )}
                    <TableCell>
                      {t(`position-status.${position.status}`)}
                    </TableCell>
                    <TableCell>
                      {editable && (
                        <Button
                          size={"sm"}
                          variant={"outlined"}
                          skin={"danger"}
                          disabled={isPending}
                          onClick={() =>
                            mutate({
                              method: "delete",
                              url: `/supplier-order-position/${position.id}`,
                            })
                          }
                        >
                          {t("positions.remove")}
                        </Button>
                      )}
                      {awaitingReceipt.includes(order.status) &&
                        Number(position.quantity_open) > 0 && (
                          <Button
                            size={"sm"}
                            variant={"outlined"}
                            disabled={isPending}
                            onClick={() => setClosing(position)}
                          >
                            {t("positions.short-close")}
                          </Button>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          )}
          {editable && (
            <Flex
              gap={"10px"}
              alignItems={"flex-end"}
              style={{ marginTop: 15 }}
            >
              <div style={{ flex: 1 }}>
                <AgreementSelect
                  order={order}
                  label={t("positions.add-article")}
                  value={agreementId}
                  onChange={setAgreementId}
                />
              </div>
              <div style={{ width: 120 }}>
                <Input
                  label={t("positions.quantity")}
                  type={"number"}
                  min={0}
                  value={newQuantity}
                  onChange={(event) => setNewQuantity(event.target.value)}
                />
              </div>
              <Button
                variant={"filled"}
                disabled={isPending || agreementId === "" || newQuantity === ""}
                onClick={add}
              >
                {t("positions.add")}
              </Button>
            </Flex>
          )}
          {editable && (
            <TextV2 variant={"body1"} style={{ marginTop: 10, opacity: 0.7 }}>
              {t("positions.hint")}
            </TextV2>
          )}
        </Panel.Content>
      </Panel>
      {closing && (
        <ReasonDialog
          title={t("positions.short-close-title")}
          description={t("positions.short-close-description", {
            open: quantity(closing.quantity_open),
            title: closing.title,
          })}
          confirmLabel={t("positions.short-close")}
          isPending={isPending}
          onSubmit={(reason) =>
            mutate(
              {
                url: `/supplier-order-position/${closing.id}/short-close`,
                body: { reason },
              },
              { onSuccess: () => setClosing(null) },
            )
          }
          onClose={() => setClosing(null)}
        />
      )}
    </div>
  );
};
